<script setup lang="ts">
import type { RadioModelValue } from "../Radio/VRadio.types";
import type { VRadioTileGroupProps } from "./VRadioTile.types";
import { computed, provide, useId } from "vue";
import { choiceTileLayoutClass } from "../ChoiceTile/choiceTileLayout";
import { RADIO_TILE_GROUP_KEY } from "./VRadioTile.types";

defineOptions({
  name: "VRadioTileGroup",
});

/**
 * Group of radio tiles: illustration left, title and subtitle in the middle, radio right, or with `variant="stacked"`
 * the illustration on top and the radio left of the text. The tiles are native radios sharing one name, so the group
 * is a single tab stop and the arrow keys move and check like any radio group.
 */
const {
  ariaLabel = undefined,
  ariaLabelledby = undefined,
  name = undefined,
  layout = "stack",
  columns = 2,
  disabled = false,
  required = false,
  variant = "row",
} = defineProps<VRadioTileGroupProps>();

defineSlots<{
  default?: () => unknown
}>();

const modelValue = defineModel<RadioModelValue>({ default: null });

const generatedName = useId();

const layoutClass = computed(() => choiceTileLayoutClass(layout, variant, columns));

provide(RADIO_TILE_GROUP_KEY, {
  name: computed(() => name ?? generatedName),
  modelValue: computed(() => modelValue.value),
  disabled: computed(() => disabled),
  variant: computed(() => variant),
  select: (value: RadioModelValue) => {
    if (disabled) {
      return;
    }
    modelValue.value = value;
  },
});
</script>

<template>
  <div
    role="radiogroup"
    :aria-label="ariaLabel"
    :aria-labelledby="ariaLabelledby"
    :aria-required="required || undefined"
    :aria-disabled="disabled || undefined"
    class="@container"
  >
    <div :class="layoutClass" data-testid="radio-tile-group-layout">
      <slot />
    </div>
  </div>
</template>
